import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { Snackbar, Alert } from "@mui/material";
import DashboardNavbar from "../dashboardNavbar";
import {
  SectionHead,
  SetTabs,
  SetTab,
  Tag,
  TagDot,
  CellPrimary,
  CellSub,
} from "../ui/primitives";
import DataTable from "../ui/DataTable";
import { useConfirm } from "../ui/ConfirmProvider";
import AdminUsersRowActionsMenu from "./AdminUsersRowActionsMenu";
import {
  fetchApplicationUsers,
  fetchApplicationUserDetail,
  updateApplicationUserActive,
} from "../services/adminUsersApi";
import { updateUserDemoFlag } from "../services/demoPolicyAdminApi";
import {
  ADMIN_USERS_LIST_DEFAULTS,
  ADMIN_USERS_SORT_FIELDS,
  ADMIN_USER_KINDS,
  buildAdminUsersListCacheKey,
  formatAdminUserName,
  getNextAdminUsersSort,
  resolveApplicationUserKind,
  sortAdminUsersLocally,
} from "../services/adminUsersListApi";
import { resolveAdminUsersGuards } from "../services/adminUsersAccess";
import { getCache, setCache, clearCacheByPrefix } from "../ui/listCache";
import AdminUserDetailSheet from "./AdminUserDetailSheet";
import { SYSTEM_TABS } from "./adminSectionTabs";

const PAGE_SIZE = 10;

const Shell = styled.div`
  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};
  @media (max-width: 768px) {
    padding: 0 14px 28px;
  }
`;

const Wrap = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.gap};
`;

const Hint = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.rMd};
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surface};
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.textMuted};
  line-height: 1.45;
`;

const ErrorNote = styled.div`
  font-size: ${({ theme }) => theme.fsSm};
  color: ${({ theme }) => theme.danger};
  line-height: 1.45;
`;

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5em;
  padding: 0 7px;
  height: 20px;
  border-radius: ${({ theme }) => theme.rPill};
  font-size: ${({ theme }) => theme.fsMicro};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ $on, theme }) => ($on ? theme.accentStrong : theme.textMuted)};
  background: ${({ $on, theme }) =>
    $on
      ? `color-mix(in oklch, ${theme.accent} 14%, ${theme.surface})`
      : theme.surface3};
  box-shadow: inset 0 0 0 1px
    ${({ $on, theme }) =>
      $on
        ? `color-mix(in oklch, ${theme.accent} 22%, transparent)`
        : `color-mix(in oklch, ${theme.border} 80%, transparent)`};
`;

const TAB_ITEMS = [
  { kind: ADMIN_USER_KINDS.instructor, label: "Instructor users" },
  { kind: ADMIN_USER_KINDS.learner, label: "Learner users" },
  { kind: ADMIN_USER_KINDS.system, label: "System users" },
];

function formatTabCount(value) {
  if (value == null) {
    return "…";
  }
  return value.toLocaleString();
}

function readCachedKindTotal(scopeId, kind) {
  for (const size of [PAGE_SIZE, 1]) {
    const cacheKey = buildAdminUsersListCacheKey("system", scopeId, {
      kind,
      page: 0,
      size,
      keyword: "",
    });
    const cached = getCache(cacheKey);
    if (cached?.totalElements != null) {
      return cached.totalElements;
    }
  }
  return null;
}

const TAB_COPY = {
  [ADMIN_USER_KINDS.instructor]: {
    emptyText: "No instructor accounts found.",
    searchPlaceholder: "Search instructors by name or email…",
    defaultRole: "Instructor",
  },
  [ADMIN_USER_KINDS.learner]: {
    emptyText: "No learner accounts found.",
    searchPlaceholder: "Search learners by name or email…",
    defaultRole: "Learner",
  },
  [ADMIN_USER_KINDS.system]: {
    emptyText: "No system users found.",
    searchPlaceholder: "Search system users by name or email…",
    hint: "StudyAK platform operators. These accounts manage the whole platform",
  },
};

export default function SystemApplicationUsers() {
  const askConfirmation = useConfirm();
  const userId = localStorage.getItem("userId");
  const { selfBlockedMessage } = resolveAdminUsersGuards({});
  const [searchParams, setSearchParams] = useSearchParams();
  const userKind = resolveApplicationUserKind(searchParams.get("kind"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [listPage, setListPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [sortBy, setSortBy] = useState(ADMIN_USERS_LIST_DEFAULTS.sortBy);
  const [sortDir, setSortDir] = useState(ADMIN_USERS_LIST_DEFAULTS.sortDir);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detail, setDetail] = useState(null);
  const [kindCounts, setKindCounts] = useState({
    [ADMIN_USER_KINDS.instructor]: null,
    [ADMIN_USER_KINDS.learner]: null,
    [ADMIN_USER_KINDS.system]: null,
  });
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const searchDebounceRef = useRef(null);

  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedKeyword(searchKeyword);
      setListPage(0);
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchKeyword]);

  const applyListPage = useCallback((parsed) => {
    setRows(parsed.content);
    setTotalElements(parsed.totalElements);
    setTotalPages(Math.max(parsed.totalPages, 1));
  }, []);

  const loadUsers = useCallback(async () => {
    if (!userId) {
      setRows([]);
      return;
    }

    const listParams = {
      kind: userKind,
      page: listPage,
      size: PAGE_SIZE,
      keyword: debouncedKeyword,
    };
    const cacheKey = buildAdminUsersListCacheKey("system", userId, listParams);
    const cached = getCache(cacheKey);
    if (cached) {
      applyListPage(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    setListError("");
    try {
      const parsed = await fetchApplicationUsers({
        adminUserId: userId,
        ...listParams,
      });
      applyListPage(parsed);
      setCache(cacheKey, parsed);
    } catch (error) {
      setListError("Unable to load application users right now.");
      setRows([]);
      setTotalElements(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [userId, userKind, listPage, debouncedKeyword, applyListPage]);

  const loadKindCounts = useCallback(async () => {
    if (!userId) {
      return;
    }

    const entries = await Promise.all(
      TAB_ITEMS.map(async ({ kind }) => {
        const cachedTotal = readCachedKindTotal(userId, kind);
        if (cachedTotal != null) {
          return [kind, cachedTotal];
        }

        try {
          const parsed = await fetchApplicationUsers({
            adminUserId: userId,
            kind,
            page: 0,
            size: 1,
            keyword: "",
          });
          const cacheKey = buildAdminUsersListCacheKey("system", userId, {
            kind,
            page: 0,
            size: 1,
            keyword: "",
          });
          setCache(cacheKey, parsed);
          return [kind, parsed.totalElements];
        } catch (error) {
          return [kind, null];
        }
      }),
    );

    setKindCounts(Object.fromEntries(entries));
  }, [userId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadKindCounts();
  }, [loadKindCounts]);

  useEffect(() => {
    if (!debouncedKeyword && !loading) {
      setKindCounts((prev) => ({
        ...prev,
        [userKind]: totalElements,
      }));
    }
  }, [userKind, totalElements, debouncedKeyword, loading]);

  const handleKindChange = (kind) => {
    setSearchParams({ kind });
    setListPage(0);
  };

  const handlePageChange = (nextPage) => {
    setListPage(Math.max(nextPage - 1, 0));
  };

  const handleSortChange = (columnKey) => {
    const next = getNextAdminUsersSort(columnKey, sortBy, sortDir);
    setSortBy(next.sortBy);
    setSortDir(next.sortDir);
  };

  const handleRowClick = async (row) => {
    if (!userId || !row.userId) {
      return;
    }
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailError("");
    setDetail(null);
    try {
      const payload = await fetchApplicationUserDetail({
        adminUserId: userId,
        userId: row.userId,
      });
      setDetail(payload);
    } catch (error) {
      setDetailError("Unable to load user details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleStatus = async (row) => {
    if (!userId || !row?.userId) {
      return;
    }
    if (row.userId === userId) {
      return;
    }

    const isActive = row.active === 1;
    const actionLabel = isActive ? "Disable access" : "Enable access";
    const name = formatAdminUserName(row);

    const confirmed = await askConfirmation({
      title: `${actionLabel}?`,
      message: isActive
        ? `${name} will be unable to sign in until you enable their account again.`
        : `${name} will be able to sign in again.`,
      confirmLabel: actionLabel,
      variant: isActive ? "danger" : "default",
      requireTypedConfirm: false,
    });
    if (!confirmed) {
      return;
    }

    try {
      await updateApplicationUserActive({
        adminUserId: userId,
        userId: row.userId,
        active: isActive ? 0 : 1,
      });
      clearCacheByPrefix(`admin-users:system:${userId}:`);
      await Promise.all([loadUsers(), loadKindCounts()]);
      if (detailOpen && detail?.userId === row.userId) {
        setDetail((prev) =>
          prev ? { ...prev, active: isActive ? 0 : 1 } : prev,
        );
      }
    } catch (error) {
      setListError("Unable to update account status right now.");
    }
  };

  const handleToggleDemo = async (row) => {
    if (!userId || !row?.userId) {
      return;
    }

    const isDemo = row.isDemoUser === 1;
    const actionLabel = isDemo ? "Remove demo user" : "Make demo user";
    const name = formatAdminUserName(row);

    const confirmed = await askConfirmation({
      title: `${actionLabel}?`,
      message: isDemo
        ? `${name} will return to a normal account with full (non read-only) access.`
        : `${name} will become a read-only demo account, subject to the Demo access policy.`,
      confirmLabel: actionLabel,
      variant: "default",
      requireTypedConfirm: false,
    });
    if (!confirmed) {
      return;
    }

    try {
      await updateUserDemoFlag(row.userId, isDemo ? 0 : 1);
      clearCacheByPrefix(`admin-users:system:${userId}:`);
      await loadUsers();
      setToast({
        open: true,
        severity: "success",
        message: isDemo
          ? `${name} is no longer a demo user.`
          : `${name} is now a demo user.`,
      });
    } catch (error) {
      setToast({
        open: true,
        severity: "error",
        message: error.message || "Unable to update demo status right now.",
      });
    }
  };

  const isSelfRow = (row) => row.userId === userId;

  const promptSelfBlock = () => {
    askConfirmation({
      title: "Not available",
      message: selfBlockedMessage,
      confirmLabel: "OK",
      requireTypedConfirm: false,
    });
  };

  const sortedRows = useMemo(
    () => sortAdminUsersLocally(rows, sortBy, sortDir),
    [rows, sortBy, sortDir],
  );

  const tabCopy = TAB_COPY[userKind] || TAB_COPY[ADMIN_USER_KINDS.instructor];

  return (
    <Shell>
      <DashboardNavbar menuItems={SYSTEM_TABS} />
      <div style={{ width: "min(1180px, 100%)", margin: "0 auto" }}>
        <SectionHead style={{ marginTop: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 22 }}>Application users</h2>
            <p
              style={{
                margin: "4px 0 0",
                color: "var(--text-muted, #64748b)",
                fontSize: 14,
              }}
            >
              Platform-wide accounts across all organizations. Super admin
              access only — not org Admin → Users.
            </p>
          </div>
        </SectionHead>
      </div>

      <Wrap>
        {listError ? <ErrorNote>{listError}</ErrorNote> : null}

        <SetTabs>
          {TAB_ITEMS.map(({ kind, label }) => {
            const isActive = userKind === kind;
            return (
              <SetTab
                key={kind}
                $on={isActive}
                onClick={() => handleKindChange(kind)}
              >
                <TabLabel>
                  {label}
                  <TabCount $on={isActive}>
                    {formatTabCount(kindCounts[kind])}
                  </TabCount>
                </TabLabel>
              </SetTab>
            );
          })}
        </SetTabs>

        {tabCopy.hint ? <Hint>{tabCopy.hint}</Hint> : null}

        <DataTable
          rows={sortedRows}
          loading={loading}
          rowKey={(member) => member.userId || member.email}
          emptyText={tabCopy.emptyText}
          pageSize={PAGE_SIZE}
          serverSide
          page={listPage + 1}
          totalPages={totalPages}
          totalElements={totalElements}
          onPageChange={handlePageChange}
          search
          searchValue={searchKeyword}
          onSearchChange={setSearchKeyword}
          searchPlaceholder={tabCopy.searchPlaceholder}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={handleSortChange}
          onRowClick={handleRowClick}
          actions={(member) => (
            <AdminUsersRowActionsMenu
              row={member}
              onToggleStatus={handleToggleStatus}
              onToggleDemo={handleToggleDemo}
              showDemoToggle={userKind === ADMIN_USER_KINDS.instructor}
              toggleAccessFeatureKey="action.system.user.toggleAccess"
              toggleDemoFeatureKey="action.system.user.toggleDemo"
            />
          )}
          primary={{
            header: "User",
            sortKey: ADMIN_USERS_SORT_FIELDS.name,
            label: (member) => formatAdminUserName(member),
            sub: (member) => member.email,
            short: (member) => {
              const name = formatAdminUserName(member);
              return name
                ? String(name)
                    .replace(/[^A-Za-z0-9]/g, "")
                    .slice(0, 2)
                    .toUpperCase()
                : undefined;
            },
          }}
          columns={[
            {
              header: "Role",
              sortKey: ADMIN_USERS_SORT_FIELDS.role,
              render: (member) =>
                userKind === ADMIN_USER_KINDS.system ? (
                  <div>
                    <CellPrimary>SuperAdmin</CellPrimary>
                    <CellSub>Instructor account</CellSub>
                  </div>
                ) : (
                  member.role || tabCopy.defaultRole
                ),
              muted: userKind !== ADMIN_USER_KINDS.system,
            },
            {
              header: "Status",
              sortKey: ADMIN_USERS_SORT_FIELDS.status,
              render: (member) => (
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <Tag $tone={member.active === 1 ? "green" : "gray"}>
                    <TagDot />
                    {member.active === 1 ? "Active" : "Inactive"}
                  </Tag>
                  {member.isDemoUser === 1 ? (
                    <Tag $tone="accent">
                      <TagDot />
                      Demo
                    </Tag>
                  ) : null}
                </div>
              ),
            },
          ]}
        />
      </Wrap>

      <AdminUserDetailSheet
        open={detailOpen}
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onClose={() => setDetailOpen(false)}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast((t) => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Shell>
  );
}
